import type { HTMLInputTypeAttribute, KeyboardEvent } from "react";
import { T } from "../tokens";

export interface InputLineProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  autoFocus?: boolean;
  dir?: "instrument" | "letter";
  /** HTML autocomplete hint (e.g. "name", "email", "off") */
  autoComplete?: string;
  /** iOS / Android keyboard hint */
  inputMode?:
    | "text"
    | "search"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal";
  /** Submit-on-Enter callback (mobile keyboard "go") */
  onSubmit?: () => void;
  /** Accessible label fallback when there's no visible <label> */
  ariaLabel?: string;
}

export function InputLine({
  value,
  onChange,
  placeholder,
  type = "text",
  autoFocus,
  dir = "instrument",
  autoComplete,
  inputMode,
  onSubmit,
  ariaLabel,
}: InputLineProps) {
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      inputMode={inputMode}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        borderBottom: `1px solid ${T.ink200}`,
        padding: "14px 0",
        minHeight: 44, // a11y touch target
        fontFamily: dir === "instrument" ? T.mono : T.han,
        fontWeight: 300,
        // PR-C / D14: bumped to match atom defaults; mobile must read clearly
        fontSize: dir === "instrument" ? 15 : 18,
        letterSpacing: dir === "instrument" ? "0.10em" : "0.02em",
        color: T.ink900,
        outline: "none",
      }}
    />
  );
}
